import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  FlaskConical, MapPin, Building2, GraduationCap, Car,
  Users, CheckCircle, AlertCircle, Loader2, Info, ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResearchPage = () => {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [isOptedIn, setIsOptedIn] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    university: '',
    hospital: '',
    suburb: '',
    state: '',
    travelDistance: '',
    participationType: [],
    contactPreference: 'email',
    additionalNotes: '',
  });

  const tenantName = tenant?.name || 'MedMCQ';

  useEffect(() => {
    document.title = `Research Participation | ${tenantName}`;
    // Load existing preferences if any
    loadExistingPreferences();
  }, [tenantName]);

  const loadExistingPreferences = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await axios.get(`${BACKEND_URL}/api/user/research-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setFormData(prev => ({ ...prev, ...response.data }));
        setIsOptedIn(response.data.opted_in || false);
      }
    } catch (error) {
      // No existing preferences, that's fine
      console.log('No existing research preferences');
    } finally {
      setLoading(false);
    }
  };

  // Universities by state
  const universitiesByState = {
    'NSW': [
      'University of Sydney', 'University of New South Wales', 'University of Newcastle',
      'University of Wollongong', 'Western Sydney University', 'Macquarie University',
      'University of Notre Dame (Sydney)', 'Charles Sturt University'
    ],
    'VIC': [
      'University of Melbourne', 'Monash University', 'Deakin University',
      'La Trobe University', 'RMIT University', 'Swinburne University'
    ],
    'QLD': [
      'University of Queensland', 'Griffith University', 'Bond University',
      'James Cook University', 'Queensland University of Technology'
    ],
    'WA': [
      'University of Western Australia', 'Curtin University', 'Edith Cowan University',
      'Murdoch University', 'University of Notre Dame (Fremantle)'
    ],
    'SA': [
      'University of Adelaide', 'Flinders University', 'University of South Australia'
    ],
    'TAS': [
      'University of Tasmania'
    ],
    'ACT': [
      'Australian National University', 'University of Canberra'
    ],
    'NT': [
      'Charles Darwin University', 'Flinders University NT'
    ],
  };

  // Hospitals by state (sample data)
  const hospitalsByState = {
    'NSW': [
      'Royal Prince Alfred Hospital', 'Westmead Hospital', 'Liverpool Hospital',
      'John Hunter Hospital', 'Prince of Wales Hospital', 'St Vincent\'s Hospital Sydney',
      'Royal North Shore Hospital', 'Concord Hospital', 'Nepean Hospital'
    ],
    'VIC': [
      'Royal Melbourne Hospital', 'Alfred Hospital', 'Austin Hospital',
      'St Vincent\'s Hospital Melbourne', 'Monash Medical Centre', 'Box Hill Hospital'
    ],
    'QLD': [
      'Royal Brisbane Hospital', 'Princess Alexandra Hospital', 'Gold Coast University Hospital',
      'Townsville Hospital', 'Cairns Hospital'
    ],
    'WA': [
      'Royal Perth Hospital', 'Sir Charles Gairdner Hospital', 'Fiona Stanley Hospital',
      'Perth Children\'s Hospital'
    ],
    'SA': [
      'Royal Adelaide Hospital', 'Flinders Medical Centre', 'Lyell McEwin Hospital'
    ],
    'TAS': [
      'Royal Hobart Hospital', 'Launceston General Hospital'
    ],
    'ACT': [
      'Canberra Hospital', 'Calvary Hospital'
    ],
    'NT': [
      'Royal Darwin Hospital', 'Alice Springs Hospital'
    ],
  };

  const states = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

  const travelDistances = [
    { value: '0-10', label: '0-10 km' },
    { value: '10-25', label: '10-25 km' },
    { value: '25-50', label: '25-50 km' },
    { value: '50-100', label: '50-100 km' },
    { value: '100+', label: '100+ km (willing to relocate)' },
  ];

  const participationTypes = [
    { value: 'clinical_trials', label: 'Clinical Trials' },
    { value: 'surveys', label: 'Surveys & Questionnaires' },
    { value: 'interviews', label: 'Interviews' },
    { value: 'focus_groups', label: 'Focus Groups' },
    { value: 'observational', label: 'Observational Studies' },
    { value: 'data_analysis', label: 'Data Analysis Projects' },
    { value: 'medical_education', label: 'Medical Education Research' },
    { value: 'quality_improvement', label: 'Quality Improvement Projects' },
  ];

  const handleParticipationTypeChange = (value) => {
    setFormData(prev => {
      const types = prev.participationType || [];
      if (types.includes(value)) {
        return { ...prev, participationType: types.filter(t => t !== value) };
      } else {
        return { ...prev, participationType: [...types, value] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setResult(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setResult({ success: false, message: 'Please log in to save your preferences.' });
        return;
      }

      await axios.post(
        `${BACKEND_URL}/api/user/research-preferences`,
        { ...formData, opted_in: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult({ success: true, message: 'Your research preferences have been saved successfully!' });
      setIsOptedIn(true);
    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to save preferences. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOptOut = async () => {
    setSaving(true);
    setResult(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      await axios.post(
        `${BACKEND_URL}/api/user/research-preferences`,
        { opted_in: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult({ success: true, message: 'You have been opted out of research participation.' });
      setIsOptedIn(false);
    } catch (error) {
      setResult({
        success: false,
        message: 'Failed to update preferences. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-cyan-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading preferences...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Icon3D icon={FlaskConical} colour="purple" size="md" />
            <h1 className="text-3xl font-bold text-slate-900">Research Participation</h1>
          </div>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Opt in to be notified about research opportunities relevant to your location and interests.
            Your data will be kept confidential and used only to match you with appropriate studies.
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-cyan-900 mb-1">Why Participate in Research?</h3>
              <p className="text-sm text-cyan-800">
                Contributing to medical research helps advance healthcare knowledge and can provide valuable experience
                for your career. Research participation may include surveys, clinical trials, interviews, and more.
                You can opt out at any time.
              </p>
            </div>
          </div>
        </div>

        {/* Result Message */}
        {result && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            result.success
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {result.success ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span>{result.message}</span>
          </div>
        )}

        {/* Main Form */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-bold text-slate-900">Research Preferences</h2>
            <p className="text-sm text-slate-500">Tell us about your location and research interests</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* State Selection */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                State/Territory *
              </label>
              <select
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value, university: '', hospital: '' })}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 transition-colors appearance-none bg-white"
                required
              >
                <option value="">Select your state</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* University Selection */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <GraduationCap className="w-4 h-4 inline mr-2" />
                University
              </label>
              <select
                value={formData.university}
                onChange={(e) => setFormData({ ...formData, university: e.target.value })}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 transition-colors appearance-none bg-white"
                disabled={!formData.state}
              >
                <option value="">Select your university</option>
                {formData.state && universitiesByState[formData.state]?.map(uni => (
                  <option key={uni} value={uni}>{uni}</option>
                ))}
                <option value="other">Other</option>
              </select>
            </div>

            {/* Hospital Selection */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-2" />
                Affiliated Hospital
              </label>
              <select
                value={formData.hospital}
                onChange={(e) => setFormData({ ...formData, hospital: e.target.value })}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 transition-colors appearance-none bg-white"
                disabled={!formData.state}
              >
                <option value="">Select your hospital (optional)</option>
                {formData.state && hospitalsByState[formData.state]?.map(hosp => (
                  <option key={hosp} value={hosp}>{hosp}</option>
                ))}
                <option value="other">Other</option>
              </select>
            </div>

            {/* Suburb */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Suburb/Postcode
              </label>
              <input
                type="text"
                value={formData.suburb}
                onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder="Enter your suburb or postcode"
              />
            </div>

            {/* Travel Distance */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Car className="w-4 h-4 inline mr-2" />
                Maximum Travel Distance
              </label>
              <select
                value={formData.travelDistance}
                onChange={(e) => setFormData({ ...formData, travelDistance: e.target.value })}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 transition-colors appearance-none bg-white"
              >
                <option value="">Select maximum travel distance</option>
                {travelDistances.map(dist => (
                  <option key={dist.value} value={dist.value}>{dist.label}</option>
                ))}
              </select>
            </div>

            {/* Participation Types */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                <Users className="w-4 h-4 inline mr-2" />
                Types of Research Interested In
              </label>
              <div className="grid md:grid-cols-2 gap-3">
                {participationTypes.map(type => (
                  <label
                    key={type.value}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                      formData.participationType?.includes(type.value)
                        ? 'border-cyan-500 bg-cyan-50'
                        : 'border-slate-200 hover:border-cyan-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.participationType?.includes(type.value) || false}
                      onChange={() => handleParticipationTypeChange(type.value)}
                      className="w-5 h-5 rounded border-slate-300 text-cyan-500 focus:ring-cyan-500"
                    />
                    <span className="text-sm font-medium text-slate-700">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Additional Information
              </label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                placeholder="Any specific interests, availability, or other information researchers should know..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-4 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin relative z-10" />
                    <span className="relative z-10">Saving...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 relative z-10" />
                    <span className="relative z-10">{isOptedIn ? 'Update Preferences' : 'Opt In to Research'}</span>
                  </>
                )}
              </button>

              {isOptedIn && (
                <button
                  type="button"
                  onClick={handleOptOut}
                  disabled={saving}
                  className="py-4 px-6 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-all"
                >
                  Opt Out
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Privacy Note */}
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-500">
            Your information is stored securely and will only be used to match you with relevant research opportunities.
            You can update or remove your preferences at any time.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default ResearchPage;
